// Dependencies
import styled, { css, keyframes } from 'styled-components'

// Types
import { PriceTableCardSelectorSlideDirections } from './pricetable-card.types'

// Utils
import { MediaQueries } from '../../utils/breakpoints'

export const PricetableCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #fff;
  padding: 24px;
  gap: 24px;
  border-radius: 16px;
  position: relative;
`

export const PricetableCardHeader = styled.div`
  display: flex;
  flex-direction: column;
`

export const PricetableCardHeaderLabel = styled.p`
  font-family: 'Source Sans 3', sans-serif;
  font-size: 32px;
  line-height: 110%;
  font-weight: 600;
  text-transform: uppercase;
  color: #7c8195;

  ${MediaQueries.amedium} {
    font-size: 16px;
  }
`

type PricetableCardTitleStyleProps = {
  color: string
}

export const PricetableCardTitle = styled.h4<PricetableCardTitleStyleProps>`
  font-family: 'Source Sans 3', sans-serif;
  font-size: 32px;
  font-weight: 900;
  line-height: 36px;
  color: ${({ color }) => color};
`

export const PricetableCardHeaderBenefitsSummaryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  height: 72px;

  ${MediaQueries.amedium} {
    height: auto;
  }
`

type PricetableCardHeaderBenefitsSummaryLabelStyleProps = {
  color: string
}

export const PricetableCardHeaderBenefitsSummaryLabel = styled.p<PricetableCardHeaderBenefitsSummaryLabelStyleProps>`
  font-family: 'Source Sans 3', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${({ color }) => color};

  ${MediaQueries.notebook} {
    white-space: nowrap;
  }
`

type PricetableCardHeaderBenefitsSummarySeparatorStyleProps = {
  color: string
}

export const PricetableCardHeaderBenefitsSummarySeparator = styled.div<PricetableCardHeaderBenefitsSummarySeparatorStyleProps>`
  display: block;
  height: 1px;
  width: 100%;
  background-color: ${({ color }) => color};
`

export const PricetableHeaderIndicatorWrapper = styled.div`
  position: relative;
  width: 100%;
`

export const PricetableHeaderIndicator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #eceeee;
`

type PricetableHeaderIndicatorLabel = {
  color: string
}

export const PricetableHeaderIndicatorLabel = styled.p<PricetableHeaderIndicatorLabel>`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);

  background-color: ${({ color }) => color};
  padding: 0 6px !important;
  color: #eceeee;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;

  text-transform: uppercase;

  z-index: 2;
`

export const PricetableCardChipsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  position: absolute;
  top: 0;
  right: 24px;
`
type PricetableCardChipItemStyleProps = {
  color: string
}

export const PricetableCardChipItem = styled.div<PricetableCardChipItemStyleProps>`
  display: block;
  padding: 10px 16px;
  border-radius: 0 0 12px 12px;
  background-color: ${({ color }) => color};
`

type PricetableCardChipItemLabelStyleProps = {
  color: string
}

export const PricetableCardChipItemLabel = styled.p<PricetableCardChipItemLabelStyleProps>`
  font-family: 'Source Sans 3', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ color }) => color};
  text-transform: uppercase;
`

export const PricetableCardChip = styled.p`
  color: #eceeee;
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`

export const PricetableCardPriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const PricetableCardSubPrice = styled.p`
  color: #9c9fb0;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;

  i {
    text-decoration: line-through;
    font-style: normal;
  }
`

type PricetableCardMainPriceStyleProps = {
  color: string
}

export const PricetableCardMainPrice = styled.p<PricetableCardMainPriceStyleProps>`
  font-family: 'Source Sans 3', sans-serif;
  font-size: 60px;
  font-weight: 900;
  line-height: 64px;
  letter-spacing: -0.04em;
  color: ${({ color }) => color};
  white-space: nowrap;
`

type PricetableCardActionButtonStyleProps = {
  color: string
}

export const PricetableCardActionButton = styled.button<PricetableCardActionButtonStyleProps>`
  display: flex;
  padding: 16px 0;
  align-items: center;
  justify-content: center;

  border: none;
  border-radius: 100px;
  background-color: ${({ color }) => color};
`

type PricetableCardActionButtonLabel = {
  color: string
}

export const PricetableCardActionButtonLabel = styled.p`
  width: 100%;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: ${({ color }) => color};

  ${MediaQueries.amedium} {
    font-size: 24px;
    font-weight: 700;
  }
`

export const PricetableCardInformation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

export const PricetableCardListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

export const PricetableCardListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

type PricetableCardListItemLabelStyleProps = {
  isDisabled: boolean
}

export const PricetableCardListItemLabel = styled.p<PricetableCardListItemLabelStyleProps>`
  font-family: 'Source Sans 3', sans-serif;
  font-size: 14px;
  line-height: 16px;

  ${({ isDisabled }) =>
    !!isDisabled &&
    css`
      color: #9c9fb0;
    `}

  ${({ isDisabled }) =>
    !isDisabled &&
    css`
      color: #25272d;
    `}
`

export const PricetableCardInformationKnowMoreWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  cursor: pointer;
`

export const PricetableCardInformationKnowMoreLabel = styled.p`
  font-family: 'Source Sans 3', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #3a80cf;
  margin: 0 auto;
  cursor: pointer;
`

export const PricetableCardSeparator = styled.div`
  display: block;
  height: 1px;
  width: 100%;
  background-color: #f0f1f7;
`

const slideInFromRight = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }

  100% {
    transform: translateX(0);
    opacity: 1;
  }
`

const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }

  100% {
    transform: translateX(0);
    opacity: 1;
  }
`

export const PriceTableCardSelectorContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 32px;

  z-index: 2;
`

type PriceTableCardSelectorContentWrapperStyleProps = {
  slideDirection: PriceTableCardSelectorSlideDirections
  isActive: boolean
}

export const PriceTableCardSelectorContentWrapper = styled.div<PriceTableCardSelectorContentWrapperStyleProps>`
  position: relative;

  display: ${({ isActive }) => (!!isActive ? 'flex' : 'none')};
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  animation: ${({ slideDirection }) =>
      slideDirection === 'right' ? slideInFromRight : slideInFromLeft}
    0.5s ease;

  > div {
    flex: 1;
  }
`

export const PriceTableCardSelectorSliderWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
  gap: 32px;
`

export const PriceTableCardSelectorActionIcons = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`

export const PriceTableCardSelectorSlider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

type PriceTableCardSelectorSliderDot = {
  isActive: boolean
}

export const PriceTableCardSelectorSliderDot = styled.div<PriceTableCardSelectorSliderDot>`
  height: 8px;
  width: 8px;
  border-radius: 200px;
  flex-shrink: 0;

  cursor: pointer;

  transition: width 0.5s ease-in-out;

  ${({ isActive }) =>
    !!isActive
      ? css`
          background-color: #fff;
        `
      : css`
          background-color: #666b7b;
        `}
`

export const PriceTableCardSelectorIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: #ed002b;
  border-radius: 4px;
  opacity: 0.9;
  transition: opacity 0.2s ease-in-out;

  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`

export const PriceTableIaLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  gap: 8px;
`

type PriceTableCourseInformationLabelStyleProps = {
  variant: 'small' | 'large'
  hasHelperText?: boolean
}

export const PriceTableCourseInformationLabel = styled.div<PriceTableCourseInformationLabelStyleProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: relative;
  gap: 8px;
  border-radius: 100px;

  &:hover {
    > .tooltip {
      visibility: visible;
      opacity: 1;
    }
  }

  ${({ variant }) =>
    variant === 'small' &&
    css`
      font-family: 'Source Sans 3';
      font-style: normal;
      font-size: 16px;
      font-weight: 700;
      color: #242424;
      background: #f1f1f1;
      padding: 8px 20px 8px 16px;
    `}

  ${({ variant }) =>
    variant === 'large' &&
    css`
      padding: 12px 20px 12px 16px;
      font-family: 'Source Sans 3';
      font-style: normal;
      font-size: 14px;
      font-weight: 400;
      color: #fff;
      background: linear-gradient(91deg, #9c2ee5 0%, #1860cc 99.9%);
    `}
`

export const PriceTableCourseInformationIcon = styled.img`
  width: 16px;
`

type SeparatorLineStyleProps = {
  color: string
}

export const SeparatorLine = styled.div<SeparatorLineStyleProps>`
  height: 1px;
  width: 100%;
  background: ${({ color }) => color};
`

export const TooltipText = styled.span`
  visibility: hidden;
  height: fit-content;
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 5px;
  padding: 5px;
  position: absolute;
  z-index: 1;
  right: 18px;
  top: -32px;
  opacity: 0;
  transition: all 0.3s;
  font-family: 'Source Sans 3', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`
